import React, { useContext, useRef, useState, useEffect } from "react";
import Button from "../Components/Button";
import ReportsLayout from "./ReportsLayout";
import useSalesSummary from "../hooks/useSalesSummary";
import useAllEventsFilterDate from "../hooks/useAllEventsFilterDate";
import { FilterContext } from "../FilterContextProvider";
import ReactToPrint from "react-to-print";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { exportToCSV } from "../Utility/Csv";
import { UserContext } from "../Auth/UserContextProvider";
import EventFilter from "../Sales/EventFilter";

const AllProducts = () => {
  const { filterData, setFilterData } = useContext(FilterContext);
  const {
    productsToRender,
    totalCOGS,
    grossSales,
    eventIds: salesSummaryEventIds,
    setEventIds: setSalesSummaryEventIds,
  } = useSalesSummary("");
  const {
    allEventsFilterDate: allEvents,
    eventIds: allEventsEventIds,
    setEventIds: setAllEventsEventIds,
  } = useAllEventsFilterDate("show");
  const [startingEvents, setStartingEvents] = useState({});
  const printRef = useRef();
  const { isAdmin } = useContext(UserContext);
  const [eventFilterDate, setEventFilterDate] = useState({ start_date: "", end_date: "", date_button: "" });

  useEffect(() => {
    if (JSON.stringify(salesSummaryEventIds) !== JSON.stringify(allEventsEventIds)) {
      setAllEventsEventIds(salesSummaryEventIds);
    }
  }, [salesSummaryEventIds, allEventsEventIds, setAllEventsEventIds]);

  const setEventIds = (eventIds) => {
    setSalesSummaryEventIds(eventIds);
    setAllEventsEventIds(eventIds);
  };

  const unitsSold = productsToRender.reduce((a, b) => {
    return a + b.sold;
  }, 0);

  const grossAttendance = allEvents.reduce((a, b) => {
    return a + b.attendance;
  }, 0);

  let perCap = 0;

  if (grossSales && grossAttendance) {
    perCap = grossSales / grossAttendance;
  }

  const productCategories = [...new Set(productsToRender.map((product) => `${product.category.name}`))];

  productCategories.sort((a) => (a === "Clothing" ? 1 : -1)).sort((a) => (a === "Music" ? 1 : -1));

  const productNames = [...new Set(productsToRender.map((product) => `${product.name}*${product.category.name}`))];

  const prodCategoriesArray = productCategories.map(function (value) {
    return {
      name: value,
      total_sold: productsToRender
        .filter((one) => one.category.name === value)
        .reduce((a, b) => {
          return a + b.sold;
        }, 0),
      total_sales: productsToRender
        .filter((one) => one.category.name === value)
        .reduce((a, b) => {
          return a + b.gross_sales;
        }, 0),
      total_cost: productsToRender
        .filter((one) => one.category.name === value)
        .reduce((a, b) => {
          return a + b.gross_cost;
        }, 0),
    };
  });

  const prodNamesArray = productNames.map(function (value) {
    return {
      name: value.split("*")[0],
      category: value.split("*")[1],
      total_sold: productsToRender
        .filter((one) => one.name === value.split("*")[0] && one.category.name === value.split("*")[1])
        .reduce((a, b) => {
          return a + b.sold;
        }, 0),
      total_sales: productsToRender
        .filter((one) => one.name === value.split("*")[0] && one.category.name === value.split("*")[1])
        .reduce((a, b) => {
          return a + b.gross_sales;
        }, 0),
      total_cost: productsToRender
        .filter((one) => one.name === value.split("*")[0] && one.category.name === value.split("*")[1])
        .reduce((a, b) => {
          return a + b.gross_cost;
        }, 0),
      ids: productsToRender
        .filter((one) => one.name === value.split("*")[0] && one.category.name === value.split("*")[1])
        .reduce((a, b) => {
          return `${a}&product_ids[]=` + b.id;
        }, 0),
    };
  });

  const productTotals = prodNamesArray
    .filter((prodName) => prodName.total_sold !== 0)
    .map((prodName) => ({
      Name: `${prodName.name} - Totals`,
      Category: prodName.category,
      Units_Sold: prodName.total_sold,
      Average_Price: prodName.total_sales / prodName.total_sold,
      Gross_Sales: prodName.total_sales,
      Cost: prodName.total_cost,
      Gross_Profit: prodName.total_sales - prodName.total_cost,
      Percentage_of_Gross: prodName.total_sales / grossSales,
      Average_Units_per_Show: prodName.total_sold / allEvents.length,
      Per_Cap: (prodName.total_sales / grossSales) * perCap,
    }));

  const totals = {
    Name: `Grand Totals`,
    Category: "",
    Units_Sold: unitsSold,
    Average_Price: "",
    Gross_Sales: grossSales,
    Cost: totalCOGS,
    Gross_Profit: grossSales - totalCOGS,
    Percentage_of_Gross: "",
    Average_Units_per_Show: "",
    Per_Cap: perCap,
  };

  const combined = productTotals.concat(totals);

  return (
    <ReportsLayout>
      <div className="mt-8 mx-8 py-10 px-12 bg-white rounded-lg border">
        {productsToRender.length > 0 && (
          <div className="flex justify-end text-sm">
            <ReactToPrint
              trigger={() => <Button className="button" text="Print"></Button>}
              content={() => printRef.current}
            />
            <Button
              className="button button-white ml-2"
              text="Export"
              onClick={() => exportToCSV(combined, `${filterData.artist_name}_product_totals_report`)}
            ></Button>
          </div>
        )}
        <div className="max-w-max">
          <Link to="/reports">
            <div className="-mt-6 mb-6 font-medium text-black text-sm">
              <FontAwesomeIcon icon={faArrowLeft}></FontAwesomeIcon> Back to reports
            </div>
          </Link>
        </div>
        <div className="flex flex-col text-sm w-1/2">
          <span className="text-xs pl-12">Date range/show</span>
          <EventFilter
            events={allEvents}
            eventsToRender={startingEvents}
            setEventsToRender={setStartingEvents}
            filterData={filterData}
            eventIds={salesSummaryEventIds}
            eventFilterDate={eventFilterDate}
            setEventFilterDate={setEventFilterDate}
            setEventIds={setEventIds}
            setFilterData={setFilterData}
          ></EventFilter>
        </div>
        <div ref={printRef} className="mt-8 mx-8 text-black">
          <div className="flex justify-between">
            <div className="mb-2 border-2 border-black p-2 min-w-max">
              <div className="text-xl font-extrabold">TM Sales Report</div>
              <div className="text-lg font-extrabold">Product Totals</div>
              <div className="flex">
                <div className="text-base font-normal italic">
                  <div>Artist:</div>
                  <div>Start Date:</div>
                  <div>End Date:</div>
                </div>
                <div className="ml-4 text-base font-normal">
                  <div className="text-lg font-semibold">{filterData.artist_name}</div>
                  <div className="align-text-top">
                    {filterData.start_date?.slice(5, 7)}/{filterData.start_date?.slice(8, 10)}/
                    {filterData.start_date?.slice(0, 4)}
                  </div>
                  <div>
                    {filterData.end_date?.slice(5, 7)}/{filterData.end_date?.slice(8, 10)}/
                    {filterData.end_date?.slice(0, 4)}
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute right-36 mb-2 border-2 border-black font-semibold p-2 min-w-max">
              <div className="flex">
                <div className="text-right italic">
                  <div>Units Sold:</div>
                  <div>Gross Sales:</div>
                  <div>Cost:</div>
                  <div>Gross Profit:</div>
                  <div>Per Cap:</div>
                  <div>Average Gross Per Show:</div>
                  <div>Number of Shows:</div>
                </div>
                <div className="ml-4 text-base text-right">
                  <div>{unitsSold?.toLocaleString()}</div>
                  <div>
                    {grossSales?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {totalCOGS?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {(grossSales - totalCOGS)?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {perCap?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </div>
                  <div>
                    {allEvents.length
                      ? (grossSales / allEvents.length)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })
                      : 0}
                  </div>
                  <div>{allEvents.length}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="w-full mt-20 text-sm">
            <div className="text-xs mb-4 font-light">
              <div className="text-right h-12 flex w-full">
                <div className="w-1/4 text-left">PRODUCT</div>
                <div className="w-1/12">UNITS SOLD</div>
                <div className="w-1/10">AVERAGE PRICE</div>
                <div className="w-1/8">GROSS SALES</div>
                <div className="w-1/8">COST</div>
                <div className="w-1/8">GROSS PROFIT</div>
                <div className="w-1/10">PERCENTAGE OF GROSS</div>
                <div className="w-1/12">AVERAGE UNITS PER SHOW</div>
                <div className="w-1/12">PER CAP</div>
              </div>
            </div>
            {prodCategoriesArray
              .filter((categoryName) => categoryName.total_sold !== 0)
              .map((categoryName, k) => {
                return (
                  <div key={categoryName.name}>
                    <div>
                      {prodNamesArray
                        .filter((prodName) => prodName.total_sold !== 0)
                        .map((prodName, j) => {
                          return (
                            <div key={prodName.name.concat(prodName.category)}>
                              {prodName.category === categoryName.name && (
                                <div key={prodName.name} className="flex text-right border-b-2 border-black">
                                  {isAdmin ? (
                                    <div className="w-1/4 text-left text-blue-500">
                                      <Link to={`/reports/sales/totals/${prodName.ids}`}>{prodName.name} Totals:</Link>
                                    </div>
                                  ) : (
                                    <div className="w-1/4 text-left">{prodName.name} Totals:</div>
                                  )}
                                  <div id={`product_name_total_sold_${j}`} className="w-1/12">
                                    {prodName.total_sold?.toLocaleString()}
                                  </div>
                                  <div id={`product_name_average_price_${j}`} className="w-1/10">
                                    {(prodName.total_sales / prodName.total_sold)?.toLocaleString("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    })}
                                  </div>
                                  <div id={`product_name_total_sales_${j}`} className="w-1/8">
                                    {prodName.total_sales?.toLocaleString("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    })}
                                  </div>
                                  <div id={`product_name_total_cost_${j}`} className="w-1/8">
                                    {prodName.total_cost?.toLocaleString("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    })}
                                  </div>
                                  <div id={`product_name_total_profit_${j}`} className="w-1/8">
                                    {(prodName.total_sales - prodName.total_cost)?.toLocaleString("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    })}
                                  </div>
                                  <div id={`product_name_total_percentage_of_gross_${j}`} className="w-1/10">
                                    {((prodName.total_sales / grossSales) * 100)?.toLocaleString(undefined, {
                                      maximumFractionDigits: 2,
                                      minimumFractionDigits: 2,
                                    })}
                                    %
                                  </div>
                                  <div id={`product_name_total_average_units_per_show_${j}`} className="w-1/12">
                                    {(prodName.total_sold / allEvents.length)?.toFixed(1)}
                                  </div>
                                  <div id={`product_name_total_per_cap_${j}`} className="w-1/12">
                                    {((prodName.total_sales / grossSales) * perCap)?.toLocaleString("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    <div className="flex py-2 text-right font-bold border-b-2 border-black">
                      <div className="w-1/4 text-base text-left">{categoryName.name} Totals:</div>
                      <div id={`category_name_total_sold_${k}`} className="w-1/12">
                        {categoryName.total_sold?.toLocaleString()}
                      </div>
                      <div id={`category_name_average_price_${k}`} className="w-1/10">
                        {(categoryName.total_sales / categoryName.total_sold)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div className="w-1/8">
                        {categoryName.total_sales?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`category_name_total_cost_${k}`} className="w-1/8">
                        {categoryName.total_cost?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`category_name_total_profit_${k}`} className="w-1/8">
                        {(categoryName.total_sales - categoryName.total_cost)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                      <div id={`category_name_total_percentage_of_gross_${k}`} className="w-1/10">
                        {((categoryName.total_sales / grossSales) * 100)?.toLocaleString(undefined, {
                          maximumFractionDigits: 2,
                          minimumFractionDigits: 2,
                        })}
                        %
                      </div>
                      <div id={`category_name_total_average_units_per_show_${k}`} className="w-1/12">
                        {(categoryName.total_sold / allEvents.length)?.toFixed(1)}
                      </div>
                      <div id={`category_name_total_per_cap_${k}`} className="w-1/12">
                        {((categoryName.total_sales / grossSales) * perCap)?.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            <div className="flex mt-4 pt-2 text-right border-t-2 border-black">
              <div className="w-1/4 text-2xl italic font-bold text-left">Totals:</div>
              <div id="gross_sold" className="w-1/12">
                <div>Units Sold</div>
              </div>
              <div className="w-1/10"></div>
              <div className="w-1/8">
                <div>Gross Sales</div>
              </div>
              <div id="gross_cost" className="w-1/8">
                <div>Cost</div>
              </div>
              <div id="gross_profit" className="w-1/8">
                <div>Gross Profit</div>
              </div>
              <div id="gross_percentage_of_gross" className="w-1/10">
                <div>Number of Shows</div>
              </div>
              <div id="gross_average_units_per_show" className="w-1/12">
                <div>Average Per Show</div>
              </div>
              <div id="gross_per_cap" className="w-1/12">
                <div>Per Cap</div>
              </div>
            </div>
            <div className="flex text-right font-bold">
              <div className="w-1/4 text-2xl italic font-bold"></div>
              <div id="gross_sold" className="w-1/12">
                {unitsSold?.toLocaleString()}
              </div>
              <div className="w-1/10"></div>
              <div className="w-1/8">
                {grossSales?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_cost" className="w-1/8">
                {totalCOGS?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_profit" className="w-1/8">
                {(grossSales - totalCOGS)?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
              <div id="gross_percentage_of_gross" className="w-1/10">
                {allEvents.length}
              </div>
              <div id="gross_average_units_per_show" className="w-1/12">
                {allEvents.length
                  ? (grossSales / allEvents.length)?.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })
                  : 0}
              </div>
              <div id="gross_per_cap" className="w-1/12">
                {perCap?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ReportsLayout>
  );
};

export default AllProducts;
